export const SHAPE_TOOLBAR_GAP_ABOVE = 60;
export const SHAPE_TOOLBAR_VIEWPORT_PADDING = 12;
export const SHAPE_TOOLBAR_FALLBACK_SIZE = { width: 480, height: 48 };

export type Point = { x: number; y: number };
export type Size = { width: number; height: number };
export type Rect = Point & Size;
export function centerAnchoredToolbarRect(centerX: number, topY: number, size: Size): Rect {
  const halfW = size.width / 2;
  return { x: centerX - halfW, y: topY, width: size.width, height: size.height };
}

export function rectFitsInViewport(
  rect: Rect,
  viewport: Size,
  padding: number,
): boolean {
  if (viewport.width <= 0 || viewport.height <= 0) return true;
  return (
    rect.x >= padding
    && rect.y >= padding
    && rect.x + rect.width <= viewport.width - padding
    && rect.y + rect.height <= viewport.height - padding
  );
}

export function clampCenterAnchoredToolbar(
  centerX: number,
  topY: number,
  size: Size,
  viewport: Size,
  padding: number,
): Point {
  const halfW = size.width / 2;
  const minCenterX = padding + halfW;
  const maxCenterX = Math.max(minCenterX, viewport.width - padding - halfW);
  const minTopY = padding;
  const maxTopY = Math.max(minTopY, viewport.height - padding - size.height);

  return {
    x: Math.min(Math.max(centerX, minCenterX), maxCenterX),
    y: Math.min(Math.max(topY, minTopY), maxTopY),
  };
}

export function resolveShapeToolbarPlacement(input: {
  anchorCenterX: number;
  anchorTopY: number;
  dragOffset: Point;
  toolbarSize: Size;
  viewport: Size;
  padding?: number;
}): { centerX: number; topY: number; forgotDragOffset: boolean } {
  const padding = input.padding ?? SHAPE_TOOLBAR_VIEWPORT_PADDING;
  const { anchorCenterX, anchorTopY, dragOffset, toolbarSize, viewport } = input;
  const hasDragOffset = dragOffset.x !== 0 || dragOffset.y !== 0;

  if (hasDragOffset) {
    const offsetCenterX = anchorCenterX + dragOffset.x;
    const offsetTopY = anchorTopY + dragOffset.y;
    const offsetRect = centerAnchoredToolbarRect(offsetCenterX, offsetTopY, toolbarSize);
    if (rectFitsInViewport(offsetRect, viewport, padding)) {
      return { centerX: offsetCenterX, topY: offsetTopY, forgotDragOffset: false };
    }
  }

  const clamped = clampCenterAnchoredToolbar(
    anchorCenterX,
    anchorTopY,
    toolbarSize,
    viewport,
    padding,
  );
  return {
    centerX: clamped.x,
    topY: clamped.y,
    forgotDragOffset: hasDragOffset,
  };
}
